import type { AnalyticsDestination, AnalyticsTraits, EventProperties } from "@/lib/analytics/analytics.types";
import { isSentinelValue } from "@/lib/env";
import type { OkAny } from "@/types/OkAny.type";

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
  }
}

const MEASUREMENT_ID = process.env.NEXT_PUBLIC_GA4_MEASUREMENT_ID;

const enabled = (): boolean =>
  typeof window !== "undefined" &&
  !!MEASUREMENT_ID &&
  !isSentinelValue(MEASUREMENT_ID) &&
  process.env.NODE_ENV !== "test";

/**
 * Thin gtag() wrapper that seeds a proper gtag stub when the google-tags-init
 * script hasn't fired yet (strategy="afterInteractive" may lose the race with
 * React useEffect on first mount). The stub uses a regular `function` (not an
 * arrow function) so `arguments` is an Arguments object — the same format that
 * gtag.js expects when dequeuing from dataLayer. The real init script will
 * overwrite window.gtag and window.dataLayer = window.dataLayer || [] will
 * preserve the already-queued entries.
 */
const gtag = (...args: unknown[]): void => {
  if (!enabled()) return;
  if (typeof window.gtag !== "function") {
    window.dataLayer = window.dataLayer ?? [];
    window.gtag = function () {
      // biome-ignore lint/complexity/noArguments: `arguments` is intentional — we need an Arguments object (not a plain array) for gtag.js queue compatibility
      (window.dataLayer as OkAny[]).push(arguments);
    } as (...a: unknown[]) => void;
  }
  window.gtag(...args);
};

export const ga4Destination: AnalyticsDestination = {
  name: "ga4",
  isEnabled: enabled,
  pageView: (path) => {
    gtag("event", "page_view", {
      page_location: typeof window !== "undefined" ? `${window.location.origin}${path}` : path,
      send_to: MEASUREMENT_ID,
    });
  },
  track: (name, properties?: EventProperties) => {
    gtag("event", name, {
      ...(properties ?? {}),
      send_to: MEASUREMENT_ID,
    });
  },
  identify: (_traits: AnalyticsTraits) => {
    // GA4 does not support server-side user identification in the client SDK.
    // User properties could be set here with gtag('set', 'user_properties', ...)
    // but we avoid sending PII (email/id) to GA4.
  },
  reset: () => {
    // No explicit reset action for GA4 client-side; session/cookie lifecycle
    // is managed by GA4 automatically.
  },
};
